/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { Sub } from '../../../../../shared/state/state.abstract.ts';
import {
  BehaviorSubject,
  delay,
  concatMap,
  filter,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  pairwise,
  share,
  switchMap,
  take,
  tap,
  zip,
} from 'rxjs';
import { IOrderCreation } from '../../../create/states/orderDetails/orderCreation.state.ts';
import equal from 'fast-deep-equal/react';
import { navBlocker } from '../../../../../shared/components/navigation/blocker/nav.blocker.ts';
import {
  OrderDeliveryCostRes,
  orderService,
  ProductDataRes,
  ProductPriceAndDiscountRes,
  TaxesByDateRes,
} from '../../../services/order.service.ts';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler.ts';
import { orderListState } from '../orderList.state.ts';
import {
  CreatedOrdersListRes,
  editOrderService,
  IPosition,
  OrderDetailsDataRes,
  SaveOrderRes,
} from '../../../services/editOrder.service.ts';
import {
  C_Discount_Kind,
  C_Kg_Representation,
  C_Order_Error_Code,
  C_Order_Type,
  C_Sale_Unit,
  C_Vat_Code,
  C_Virtual_Position,
  SaveOrderMutationVariables,
  Wa_OrderPositionInputs,
} from '../../../../../graphql/generatedModel.ts';
import { addTotalWeight, getTotalWeight } from '../../../loaders/editOrder.loader.ts';
import { snackbarService } from '../../../../../shared/components/snackbar/service/snackbar.service.ts';
import i18n from 'i18next';
import { companyConfigService } from '../../../../../shared/services/companyConfig/companyConfig.service.ts';
import { format } from 'date-fns';
import {
  additionalOptions$,
  getOrderTypeDescription,
  IAdditionalOptions,
  IMergePosition,
  initOrderDetailsState,
  IOrderDetailsState,
  IPrepareArgsToSave,
  TArticleFromProductList,
} from './orderDetails.state.ts';
import { localeFormatterHelper } from '../../../../../shared/helpers/formatter/localeFormatter.helper.ts';

/**
 * The blank line a new order position starts from.
 *
 * Kept outside the RxJS pipeline so that a keyboard handler can insert the
 * very same object into the grid synchronously, in the keydown that asked for
 * it, and hand it to `pub.addPosition` for the state to catch up with. Until
 * the new row exists in the grid there is no cell to focus, and every
 * keystroke that arrived in that gap went to the row the user had just left,
 * or was dropped by the guard that tried to stop that (WA-3671, WA-3803).
 */
export const createEmptyPosition = (): IPosition => ({
  virtualPositionId: null,
  articleId: null,
  articleNo: null,
  description: null,
  quantity: null,
  price: 0,
  total: 0,
  discount: 0,
  vatCode: C_Vat_Code.VT5_ZERO,
  indTextDeliveryNote: null,
  indTextProduction: null,
  splittingPartsPreset: null,
  quantityPerLot: null,
  minQuantity: null,
  maxQuantity: null,
  saleUnit: null,
  weight: null,
  isChangeable: true,
  id: Date.now(),
  __reorder__: i18n.t('order.new_position'),
});

export class SubImpl extends Sub<IOrderDetailsState> {
  private loading$ = new BehaviorSubject<boolean>(false);
  private shareLoading$: Observable<boolean> = this.loading$.pipe(share());
  private saveLoading$ = new BehaviorSubject<boolean>(false);
  private shareSaveLoading$: Observable<boolean> = this.saveLoading$.pipe(share());
  private warnings$ = new BehaviorSubject<{
    warnings: IOrderCreation['warnings'] | undefined;
    linkedRecords: IOrderCreation['linkedRecords'];
  }>({ warnings: undefined, linkedRecords: undefined });
  private shareWarnings$: Observable<{
    warnings: IOrderCreation['warnings'] | undefined;
    linkedRecords: IOrderCreation['linkedRecords'];
  }> = this.warnings$.pipe(share());
  private cellsLoading$ = new BehaviorSubject<string[]>([]); // Loading by cells
  private shareCellsLoading$ = this.cellsLoading$.pipe(share());
  private barcodeLoading$ = new BehaviorSubject<boolean>(false);
  private shareBarcodeLoading$: Observable<boolean> = this.barcodeLoading$.pipe(share());
  private calcPriceError$ = new BehaviorSubject<number[]>([]);
  private shareCalcPriceError$ = this.calcPriceError$.pipe(share());
  private editedCells: Set<string> = new Set(); // Cells edited manually (format - 'idPosition_cellName')
  private deliveryCostLoading$ = new BehaviorSubject<boolean>(false);
  protected shareDeliveryCostLoading$ = this.deliveryCostLoading$.pipe(share());

  protected actionHandlers(): Observable<IOrderDetailsState> {
    return merge(
      this.queryData(),
      this.updateState(),
      this.simpleCell(),
      this.productCell(),
      this.vatCell(),
      this.quantityCell(),
      this.updatePosition(),
      this.addPosition(),
      this.addPositionByBarcode(),
      this.mergePositions(),
      this.resetOrderDataChanges(),
      this.deletePosition(),
      this.saveOrder(),
      this.updateLastEmailDetails(),
      this.selectPosition(),
    ).pipe(
      tap((state) => {
        const dirty =
          state.action !== 'emptyData' && !equal(state.orderDataBackup, state.orderData);
        state.dirty = dirty;
        navBlocker.watch(dirty);
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
      tap(({ action }) => action !== 'saveOrder' && this.loading$.next(false)),
    );
  }
  loading(): Observable<boolean> {
    return this.shareLoading$;
  }
  barcodeLoading(): Observable<boolean> {
    return this.shareBarcodeLoading$;
  }
  saveLoading(): Observable<boolean> {
    return this.shareSaveLoading$;
  }
  warnings(): Observable<{
    warnings: IOrderCreation['warnings'] | undefined;
    linkedRecords: IOrderCreation['linkedRecords'];
  }> {
    return this.shareWarnings$;
  }
  cellsLoading(): Observable<string[]> {
    return this.shareCellsLoading$;
  }
  deliveryCostLoading(): Observable<boolean> {
    return this.shareDeliveryCostLoading$;
  }
  calculationPriceError(): Observable<number[]> {
    return this.shareCalcPriceError$;
  }
  private updateState(): Observable<IOrderDetailsState> {
    return this.actionListener([
      'updateOrderData',
      'updatePositions',
      'updateState',
      'updateIsNotePinned',
      'updateIsAddressPinned',
    ]);
  }

  /* METHODS OF SECONDARY OPTIONS start */
  additionalOptions(): Observable<IAdditionalOptions> {
    return additionalOptions$.pipe(switchMap(() => merge(this.productList(), this.taxesList())));
  }
  private productList(): Observable<IAdditionalOptions> {
    return orderService.sub.filteredProductData().pipe(
      responseHandler<IAdditionalOptions['productData']>({
        errorReturnType: {
          productList: [],
          listSpecialAvailabilityStateForArticles: undefined,
          listSpecialAssortmentGroups: undefined,
        },
        customErrorHandler: () => 'errors.unknownWithReload',
      }),
      map((productData) => {
        const additionalOptionsState = additionalOptions$.getValue();
        additionalOptionsState.productData = productData;
        return additionalOptionsState;
      }),
    );
  }
  private taxesList(): Observable<IAdditionalOptions> {
    return orderService.sub.taxes().pipe(
      responseHandler<TaxesByDateRes>({
        errorReturnType: [],
      }),
      map((taxes) => {
        const additionalOptionsState = additionalOptions$.getValue();
        additionalOptionsState.taxes = taxes;
        return additionalOptionsState;
      }),
    );
  }
  /* METHODS OF SECONDARY OPTIONS end */

  /* MASTER DATA METHODS start */
  private queryData(): Observable<IOrderDetailsState> {
    return orderListState.sub.state().pipe(
      pairwise(),
      filter(
        ([prevV, currV]) =>
          (currV.action === 'selectedOrder' ||
            currV.action === 'deleteSelectedOrder' ||
            currV.action === 'deleteSelectedOrderFromList' ||
            currV.action === 'filter') &&
          (prevV.filter.date !== currV.filter.date ||
            prevV.selectedOrder?.orderId !== currV.selectedOrder?.orderId),
      ),
      switchMap(([prevListState, listState]) => {
        this.warnings$.next({ warnings: undefined, linkedRecords: undefined });
        if (!listState.selectedOrder) {
          return of({ ...initOrderDetailsState, action: 'emptyData' } as IOrderDetailsState);
        }
        return zip(this.stream$, of(listState)).pipe(
          tap(([_, listState]) => {
            this.loading$.next(true);
            const customerId = listState.selectedOrder?.customerId as string;
            const orderId = listState.selectedOrder?.orderId as string;
            editOrderService.pub.orderDetailsData({ customerId, orderId });
            const {
              productData: { listSpecialAssortmentGroups, listSpecialAvailabilityStateForArticles },
            } = additionalOptions$.getValue();

            const orderType = listState.selectedOrder?.orderType as C_Order_Type;
            const date = listState.selectedOrder?.orderDate;
            const isDateChanged =
              prevListState.selectedOrder?.orderDate !== date ||
              !listSpecialAvailabilityStateForArticles;
            const isNewCustomer = prevListState.selectedOrder?.customerId !== customerId;
            orderService.pub.filteredProductData({
              ...((isNewCustomer || !listSpecialAssortmentGroups) && { customerId }),
              isDateChanged,
              date,
              orderType,
              listSpecialAssortmentGroups,
              listSpecialAvailabilityStateForArticles,
            });

            const prevDate = prevListState.selectedOrder?.orderDate;
            if (prevDate !== date) {
              orderService.pub.taxes({
                date: date,
              });
            }
          }),
          switchMap(([state, listState]) => {
            const orderTypeDescription = getOrderTypeDescription(
              listState?.selectedOrder as CreatedOrdersListRes[number],
            );
            return editOrderService.sub.orderDetailsData().pipe(
              responseHandler<OrderDetailsDataRes | undefined>({ errorReturnType: undefined }),
              map((data) => {
                if (
                  !data ||
                  !Object.keys(data.customerInfo).length ||
                  !Object.keys(data.orderData).length
                ) {
                  // in case of error
                  const emptyState = structuredClone(initOrderDetailsState);
                  emptyState.action = 'errorData';
                  return emptyState;
                }
                const { customerInfo, orderData } = data;
                const {
                  customerNo,
                  internalOrFullName,
                  informationTip,
                  deliveryAddress,
                  discountKindId,
                  isDeliverySplitting,
                  deliverySplittingPartsCount,
                  transportSector,
                  transportSectorId,
                  id,
                  ...contacts
                } = customerInfo;
                const orderDataWithWeight = addTotalWeight({ ...orderData, orderTypeDescription });
                return {
                  ...state,
                  customerInfo: {
                    customerNo,
                    internalOrFullName,
                    informationTip,
                    deliveryAddress,
                    discountKindId,
                    isDeliverySplitting,
                    deliverySplittingPartsCount,
                    transportSector,
                    transportSectorId,
                    id,
                    contacts,
                  },
                  orderData: orderDataWithWeight,
                  orderDataBackup: structuredClone(orderDataWithWeight),
                  selectedPos: null,
                  noData: false,
                  action: 'queryData',
                } as IOrderDetailsState;
              }),
            );
          }),
        );
      }),
    );
  }
  saveOrder(): Observable<IOrderDetailsState> {
    return this.actionListener('saveOrder').pipe(
      tap(() => {
        this.saveLoading$.next(true);
      }),
      switchMap((state) => zip(orderListState.sub.sharedValues(), of(state))),
      switchMap(([{ order, lastOrderInTable }, state]) => {
        /* Freeze what is actually sent. `state.orderData` is a shared reference
        (`Pub.emit` shallow-copies, the cell reducers write into that object), so it
        keeps changing while the mutation is in flight - a late price round-trip, a
        quantity typed under the spinner. Backing up the live object on success marked
        those edits as saved and never sent them (WA-3820); this frozen copy becomes
        the backup instead. */
        const savedOrderData = structuredClone(state.orderData) as IOrderDetailsState['orderData'];
        const args = this.prepareArgsToSave({
          state: { ...state, orderData: savedOrderData },
          orderId: order?.orderId as string,
          modificationOptions: state?.params,
        });
        editOrderService.pub.saveOrder(args);
        return editOrderService.sub.saveOrder().pipe(
          responseHandler<SaveOrderRes>({
            customErrorHandler: ({ code, linkedRecords }) => {
              this.saveLoading$.next(false);
              const otherWarningsAndErrors =
                code === C_Order_Error_Code.OEC3_QUANTITY_PER_LOT ||
                code === C_Order_Error_Code.OEC4_MIN_MAX ||
                code === C_Order_Error_Code.OEC5_SPLITTING ||
                code === C_Order_Error_Code.OEC6_NOT_ENOUGH_DAYS_TO_PRODUCE ||
                code === C_Order_Error_Code.OEC7_TOUR_IS_EMPTY_BUT_NEEDED;
              const emptyPositionsError =
                code === C_Order_Error_Code.OEC2_EMPTY_POSITIONS ||
                code === C_Order_Error_Code.OEC1_EMPTY_ORDER;
              const isOutdatedOrderPositionsIds =
                code === C_Order_Error_Code.OEC8_INVALID_ORDER_POSITIONS_IDS;
              if (otherWarningsAndErrors) {
                this.warnings$.next({
                  warnings: code,
                  linkedRecords: linkedRecords as IOrderCreation['linkedRecords'],
                });
              } else if (emptyPositionsError) {
                snackbarService.pub.show({
                  type: 'error',
                  content: i18n.t('order.empty_positions_create_warning'),
                });
              } else if (isOutdatedOrderPositionsIds) {
                snackbarService.pub.show({
                  type: 'error',
                  content: i18n.t('order.order_not_saved_because_data_is_outdated'),
                  autoHideDuration: 10000,
                });
              } else {
                snackbarService.pub.show({
                  type: 'error',
                  content: i18n.t('common.server_error'),
                });
              }
              return {
                reportToSentry: !(otherWarningsAndErrors || emptyPositionsError),
              };
            },
            errorReturnType: undefined,
            quite: true,
          }),
          /* One response per save: without it this subscription outlives its request,
          so the next `pub.saveOrder(...)` - emitted above before `switchMap` tears the
          previous one down - fired a duplicate mutation for the same order. */
          take(1),
          filter((data) => {
            return !!data;
          }),
          map((result) => {
            this.saveLoading$.next(false);
            snackbarService.pub.show({
              type: 'success',
              content: i18n.t('common.successfully_saved'),
            });
            this.warnings$.next({ warnings: undefined, linkedRecords: undefined });
            /* The state as it is NOW: returning the pre-request snapshot reverted
            anything done while the save was in flight - a position added, a tour
            picked - since those reducers swap in a new `orderData` object (WA-3820). */
            const currentState = this.stream$.getValue();
            const isLeftGridDataChanged =
              currentState?.customerInfo?.customerNo !== result?.customerNo ||
              order?.orderTotal !== result?.orderTotal;
            if (order?.orderDate !== result?.orderDate) {
              orderListState.pub.deleteSelectedOrderFromList();
              if (lastOrderInTable) {
                return { ...initOrderDetailsState, action: 'emptyData' } as IOrderDetailsState;
              }
            } else if (isLeftGridDataChanged) {
              orderListState.pub.editSelectedOrder(result);
            }

            return {
              ...currentState,
              // Explicit: `actionHandlers()`'s trailing tap spares `loading$` only for
              // 'saveOrder', and `currentState` carries whatever action ran last.
              action: 'saveOrder' as const,
              orderDataBackup: savedOrderData,
              // Only what was sent is clean; a mid-flight edit keeps Save live.
              dirty: !equal(savedOrderData, currentState.orderData),
            };
          }),
        );
      }),
    );
  }
  mergePositions(): Observable<IOrderDetailsState> {
    return this.actionListener('mergePositions').pipe(
      map((oldState) => {
        const state = structuredClone(oldState);
        const positions = state?.orderData?.positions || [];

        const { mergedPositions: updatedPositions, isChanged } = positions.reduce<IMergePosition>(
          (accumValues, pos) => {
            const { mergedPositions } = accumValues;
            let { isChanged } = accumValues;
            const position = pos as IPosition;

            const existingPositionIndex = mergedPositions.findIndex(
              (p) =>
                p.articleId === position?.articleId &&
                p.discount === position.discount &&
                p.indTextDeliveryNote === position.indTextDeliveryNote &&
                p.indTextProduction === position.indTextProduction &&
                p.price === position.price &&
                p.vatCode === position.vatCode,
            );

            if (existingPositionIndex !== -1) {
              if (mergedPositions[existingPositionIndex]) {
                isChanged = true;
                mergedPositions[existingPositionIndex].quantity! += position?.quantity || 0;
                mergedPositions[existingPositionIndex].total = this.calculateTotalDependsOnSaleUnit(
                  mergedPositions[existingPositionIndex],
                );
              }
            } else {
              mergedPositions.push({ ...position });
            }

            return { mergedPositions, isChanged };
          },
          { mergedPositions: [], isChanged: false },
        );

        if (isChanged) {
          state.orderData.positions = updatedPositions;
          return state;
        }

        // when nothing changed
        snackbarService.pub.show({
          type: 'info',
          content: i18n.t('order.nothing_to_merge'),
        });
        return { ...state, action: undefined };
      }),
    );
  }
  addPositionByBarcode(): Observable<IOrderDetailsState> {
    return this.actionListener('addPositionByBarcode').pipe(
      mergeMap((oldState) => {
        const state = structuredClone(oldState);
        const {
          articleInfo,
          price: priceFromBarcode,
          quantity,
        } = state?.params as {
          articleInfo: TArticleFromProductList;
          quantity: number;
          price: number;
        };

        const newPosition = {
          virtualPositionId: null,
          articleId: articleInfo?.id,
          articleNo: articleInfo?.articleNo,
          description: articleInfo?.label,
          quantity,
          price: 0,
          total: 0,
          discount: 0,
          vatCode: articleInfo?.vatCode || C_Vat_Code.VT5_ZERO,
          indTextDeliveryNote: null,
          indTextProduction: null,
          splittingPartsPreset: null,
          quantityPerLot: articleInfo?.qtyPerLot,
          minQuantity: null,
          maxQuantity: null,
          saleUnit: articleInfo?.saleUnit,
          weight: articleInfo?.weight,
          isChangeable: true,
          id: Date.now(),
          __reorder__: `(${articleInfo?.articleNo}) ${articleInfo?.label}`,
        };

        return zip(of(newPosition), orderListState.sub.sharedValues()).pipe(
          tap(([newPosition, customerData]) => {
            orderService.pub.productData({
              orderAndArticleProps: {
                orderDate: customerData.date,
                customerId: customerData.order?.customerId!,
                articleId: newPosition?.articleId!,
                vatCode: newPosition?.vatCode,
                quantity: Number(newPosition?.quantity),
              },
            });
            this.barcodeLoading$.next(true);
          }),
          switchMap(([newPosition]) => {
            return orderService.sub.productData().pipe(
              responseHandler<ProductDataRes | 'serverError'>({ errorReturnType: 'serverError' }),
              map((values) => {
                let updatedPos = { ...newPosition } as IPosition;
                if (values === 'serverError' && newPosition?.id) {
                  this.calcPriceError$.next([...this.calcPriceError$.value, newPosition?.id]);
                } else if (typeof values === 'object') {
                  updatedPos = {
                    ...newPosition,
                    ...values,
                    price: priceFromBarcode ? priceFromBarcode : values?.price,
                  } as IPosition;
                }
                updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
                state.orderData.positions = [...(state?.orderData?.positions || []), updatedPos];
                state.selectedPos = updatedPos;
                this.barcodeLoading$.next(false);
                return { ...state };
              }),
            );
          }),
        );
      }),
    );
  }
  addPosition(): Observable<IOrderDetailsState> {
    return this.actionListener(['addPosition', 'addDeliveryPosition']).pipe(
      // 50 ms fixes the problem if the cell is in edit mode (toolbar "+" is
      // clicked mid-edit). Keyboard callers have already left edit mode and
      // pass `immediateAdd`: for them the handler runs synchronously, inside
      // the keydown that asked for the row, so the state already holds the
      // position the grid just inserted (WA-3803).
      concatMap((state) => (state.immediateAdd ? of(state) : of(state).pipe(delay(50)))),
      switchMap((state) => {
        const updatedState = structuredClone(state);
        if (updatedState.action === 'addDeliveryPosition') {
          return this.getDeliveryCost({ state, force: true });
        } else {
          // Either the position the publisher already built (an `immediate`
          // add, inserted into the grid in the same keydown) or a blank line
          // built here after the grace period; both go in front of the
          // virtual delivery-cost row.
          const newPosition = updatedState.selectedPos ?? createEmptyPosition();
          const deliveryPositionInd = updatedState.orderData.positions?.findIndex(
            (el) => el?.virtualPositionId,
          );
          if (typeof deliveryPositionInd === 'number' && deliveryPositionInd !== -1) {
            updatedState.orderData.positions?.splice(deliveryPositionInd, 0, newPosition);
          } else updatedState.orderData.positions?.push(newPosition);
          updatedState.selectedPos = newPosition;
          return of(updatedState);
        }
      }),
    );
  }
  deletePosition(): Observable<IOrderDetailsState> {
    return this.actionListener(['deletePosition', 'deleteDeliveryPosition']).pipe(
      delay(50), // fixes the problem if the cell is in edit mode
      map((state) => {
        const updatedState = structuredClone(state);
        if (updatedState.action === 'deleteDeliveryPosition') {
          updatedState.orderData.positions = updatedState.orderData.positions?.filter(
            (pos) => !pos?.virtualPositionId,
          );
        } else {
          let selectedPos: IOrderDetailsState['selectedPos'] = null;
          updatedState.orderData.positions = updatedState.orderData.positions?.filter(
            (pos, i, arr) => {
              if (pos?.id === state.selectedPos?.id) {
                const nextPos = arr?.[i + 1];
                const prevPos = arr?.[i - 1];
                if (i === 0 && arr!.length > 1) {
                  selectedPos =
                    !nextPos?.virtualPositionId && nextPos?.isChangeable ? nextPos : null;
                }
                if (i !== 0) {
                  selectedPos =
                    (!nextPos?.virtualPositionId && nextPos?.isChangeable && nextPos) ||
                    (prevPos?.isChangeable ? prevPos : null);
                }
                return false;
              } else return true;
            },
          );
          updatedState.selectedPos = selectedPos;
        }
        return updatedState;
      }),
      switchMap((state) => {
        const prevSelectedPos = this.stream$.getValue().selectedPos;
        if (
          state.action === 'deletePosition' &&
          prevSelectedPos?.articleId &&
          prevSelectedPos?.quantity &&
          prevSelectedPos?.weight
        ) {
          this.stream$.next({ ...state, action: 'updateState' });
          return this.getDeliveryCost({ state }); // Recalculate delivery data
        } else return of(state);
      }),
    );
  }

  updatePosition(): Observable<IOrderDetailsState> {
    return this.actionListener('updatePosition').pipe(
      map((state) => {
        const updatedState = structuredClone(state);
        const { selectedPos } = state;
        updatedState.orderData.positions = updatedState.orderData.positions?.map((pos) => {
          if (pos?.id === selectedPos?.id) return selectedPos;
          return pos;
        });
        return updatedState;
      }),
    );
  }

  updateLastEmailDetails(): Observable<IOrderDetailsState> {
    return this.actionListener(['updateLastEmailDetails']).pipe(
      switchMap((state) => {
        return zip(of(state), orderListState.sub.sharedValues());
      }),
      filter(([state, { order }]) => state.params.dataRecordId === order?.orderId),
      map(([state]) => {
        const updatedState = structuredClone(state);
        updatedState.orderData.datetimeOfLastEmail = state.params.datetimeOfLastEmail;
        updatedState.orderDataBackup.datetimeOfLastEmail = state.params.datetimeOfLastEmail;
        return updatedState;
      }),
    );
  }
  selectPosition(): Observable<IOrderDetailsState> {
    return this.actionListener('selectPosition').pipe(delay(10));
  }

  public clearAdditionalStateValues() {
    this.warnings$.next({ warnings: undefined, linkedRecords: undefined });
    this.calcPriceError$.next([]);
    this.cellsLoading$.next([]);
    this.barcodeLoading$.next(false);
    this.deliveryCostLoading$.next(false);
    this.editedCells.clear();
  }

  /* MASTER DATA METHODS end */

  /* ORDER CELL METHODS start */
  private quantityCell(): Observable<IOrderDetailsState> {
    return this.actionListener('quantityCell').pipe(
      mergeMap((state) => {
        const { selectedPos, action, customerInfo } = state;
        this.preUpdateCell(selectedPos);
        const isNotEditableDiscount =
          customerInfo?.discountKindId === C_Discount_Kind.DK5_QUANTITY_DISCOUNT ||
          customerInfo?.discountKindId === C_Discount_Kind.DK6_QUANTITY_DISCOUNT_GROUPS;
        const shouldFetch = Boolean(
          selectedPos?.articleNo &&
            !(
              this.editedCells.has(`${selectedPos?.id}_priceCell`) &&
              (isNotEditableDiscount || this.editedCells.has(`${selectedPos?.id}_discountCell`))
            ),
        );
        return zip(of(selectedPos), orderListState.sub.sharedValues()).pipe(
          tap(([editablePos, { order, date }]) => {
            if (shouldFetch) {
              orderService.pub.productPriceAndDiscount({
                orderAndArticleProps: {
                  articleId: editablePos?.articleId!,
                  customerId: order?.customerId!,
                  orderDate: date,
                  vatCode: editablePos?.vatCode!,
                  quantity: Number(editablePos?.quantity!),
                },
              });
              this.cellsLoading$.next([...this.cellsLoading$.value, `${editablePos?.id}_quantity`]);
            }
          }),
          switchMap(([editablePos]) => {
            if (shouldFetch) {
              return orderService.sub.articlePriceAndDiscount().pipe(
                responseHandler<ProductPriceAndDiscountRes | 'serverError'>({
                  errorReturnType: 'serverError',
                }),
                map((values) => {
                  const shouldUpdatePrice = !this.editedCells.has(`${editablePos?.id}_priceCell`);
                  const shouldUpdateDiscount = !this.editedCells.has(
                    `${editablePos?.id}_discountCell`,
                  );

                  this.cellsLoading$.next(
                    this.cellsLoading$.value.filter(
                      (item) => item !== `${editablePos?.id}_quantity`,
                    ),
                  );
                  const livePos = this.livePosition(editablePos);
                  if (!livePos) return;
                  /* The live row already carries the quantity `preUpdateCell`
                  wrote synchronously, and if the user has changed it again
                  since, that newer value is the one to keep. */
                  const updatedPos = { ...livePos };
                  if (shouldUpdatePrice) {
                    if (values === 'serverError' && editablePos?.id) {
                      updatedPos.price = 0;
                      this.calcPriceError$.next([...this.calcPriceError$.value, editablePos.id]);
                      return updatedPos;
                    } else if (typeof values === 'object' && editablePos?.id) {
                      updatedPos.price = values.price;
                      this.calcPriceError$.next(
                        this.calcPriceError$.value.filter((item) => item !== editablePos?.id),
                      );
                    }
                  }
                  if (shouldUpdateDiscount && typeof values === 'object') {
                    updatedPos.discount = values.discount;
                  }
                  return updatedPos;
                }),
              );
            }
            return of(editablePos);
          }),
          filter((updatedPos) => !!updatedPos), // if position is deleted
          map((updatedPos) => {
            const state = this.stream$.getValue();
            updatedPos!.total = this.calculateTotalDependsOnSaleUnit(updatedPos!);
            state.orderData.positions = state.orderData.positions?.map((el) =>
              el?.id === updatedPos?.id ? updatedPos! : el,
            );
            this.editedCells.add(`${updatedPos?.id}_${action}`);
            return { ...state };
          }),
        );
      }),
      switchMap((state) => {
        if (state.selectedPos?.articleId) {
          this.stream$.next({ ...state, action: 'updateState' });
          return this.getDeliveryCost({ state }); // Recalculate delivery data
        } else return of(state);
      }),
    );
  }

  private resetOrderDataChanges(): Observable<IOrderDetailsState> {
    return this.actionListener('resetOrderDataChanges').pipe(
      delay(50), // fixes the problem if the cell is in edit mode
      map((state) => {
        const updatedState = structuredClone(state);
        const { orderDataBackup } = updatedState;
        updatedState.orderData = structuredClone(orderDataBackup);
        this.calcPriceError$.next([]);
        return updatedState;
      }),
    );
  }

  private simpleCell(): Observable<IOrderDetailsState> {
    return this.actionListener([
      'priceCell',
      'discountCell',
      'splittingCell',
      'correctionValueCell',
    ]).pipe(
      map((state) => {
        const { selectedPos, action } = state;
        const updatedPos = structuredClone(selectedPos!);
        if (action === 'priceCell') {
          this.calcPriceError$.next(
            this.calcPriceError$.value.filter((item) => item !== updatedPos?.id),
          );
          updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
        }
        state.orderData.positions = state.orderData.positions?.map((el) =>
          el?.id === updatedPos?.id ? updatedPos : el,
        );
        state.selectedPos = updatedPos;
        this.editedCells.add(`${updatedPos?.id}_${action}`);
        return { ...state };
      }),
    );
  }

  private vatCell(): Observable<IOrderDetailsState> {
    return this.actionListener('vatCodeCell').pipe(
      mergeMap((state) => {
        const { selectedPos, customerInfo, action } = state;
        this.preUpdateCell(selectedPos);
        const isNotEditableDiscount =
          customerInfo?.discountKindId === C_Discount_Kind.DK5_QUANTITY_DISCOUNT ||
          customerInfo?.discountKindId === C_Discount_Kind.DK6_QUANTITY_DISCOUNT_GROUPS;
        const shouldFetch = Boolean(
          selectedPos?.articleNo &&
            !(
              this.editedCells.has(`${selectedPos?.id}_priceCell`) &&
              (isNotEditableDiscount || this.editedCells.has(`${selectedPos?.id}_discountCell`))
            ),
        );
        return zip(of(selectedPos), orderListState.sub.sharedValues()).pipe(
          tap(([editablePos, { order, date }]) => {
            if (shouldFetch) {
              orderService.pub.productPriceAndDiscount({
                orderAndArticleProps: {
                  articleId: editablePos?.articleId!,
                  customerId: order?.customerId!,
                  orderDate: date,
                  quantity: Number(editablePos?.quantity),
                  vatCode: editablePos?.vatCode!,
                },
              });
              this.cellsLoading$.next([...this.cellsLoading$.value, `${editablePos?.id}_vatCode`]);
            }
          }),
          switchMap(([editablePos]) => {
            if (shouldFetch) {
              return orderService.sub.articlePriceAndDiscount().pipe(
                responseHandler<ProductPriceAndDiscountRes | 'serverError'>({
                  errorReturnType: 'serverError',
                }),
                map((values) => {
                  const shouldUpdatePrice = !this.editedCells.has(`${editablePos?.id}_priceCell`);
                  const shouldUpdateDiscount = !this.editedCells.has(
                    `${editablePos?.id}_discountCell`,
                  );
                  this.cellsLoading$.next(
                    this.cellsLoading$.value.filter(
                      (item) => item !== `${editablePos?.id}_vatCode`,
                    ),
                  );
                  const livePos = this.livePosition(editablePos);
                  if (!livePos) return;
                  // `preUpdateCell` has already written the new VAT code in.
                  const updatedPos = { ...livePos };
                  if (shouldUpdatePrice) {
                    if (values === 'serverError' && editablePos?.id) {
                      updatedPos.price = 0;
                      updatedPos.total = 0;
                      this.calcPriceError$.next([...this.calcPriceError$.value, editablePos?.id]);
                      return updatedPos;
                    } else if (typeof values === 'object' && editablePos?.id) {
                      updatedPos.price = values.price;
                      updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
                      this.calcPriceError$.next(
                        this.calcPriceError$.value.filter((item) => item !== editablePos?.id),
                      );
                    }
                  }
                  if (shouldUpdateDiscount && typeof values === 'object') {
                    updatedPos.discount = values.discount;
                  }
                  return updatedPos;
                }),
              );
            }
            return of(editablePos);
          }),
          filter((updatedPos) => !!updatedPos), // if position is deleted
          map((updatedPos) => {
            const state = this.stream$.getValue();
            state.orderData.positions = state.orderData.positions?.map((el) =>
              el?.id === updatedPos?.id ? updatedPos! : el,
            );
            this.editedCells.add(`${updatedPos?.id}_${action}`);
            return { ...state };
          }),
        );
      }),
    );
  }

  private productCell(): Observable<IOrderDetailsState> {
    return this.actionListener('descriptionCell').pipe(
      mergeMap(({ selectedPos, action }) => {
        this.preUpdateCell(selectedPos);
        return zip(of(selectedPos), orderListState.sub.sharedValues()).pipe(
          tap(([editablePos, customerData]) => {
            const position = this.stream$.value.selectedPos;
            orderService.pub.productData({
              orderAndArticleProps: {
                orderDate: customerData.date,
                customerId: customerData.order?.customerId!,
                articleId: editablePos?.articleId!,
                vatCode: position?.vatCode!,
                quantity: Number(position?.quantity),
              },
            });
            this.cellsLoading$.next([
              ...this.cellsLoading$.value,
              `${editablePos?.id}_description`,
            ]);
          }),
          switchMap(([editablePos]) => {
            return orderService.sub.productData().pipe(
              responseHandler<ProductDataRes | 'serverError'>({ errorReturnType: 'serverError' }),
              filter(() => {
                const state = this.stream$.getValue();
                this.cellsLoading$.next(
                  this.cellsLoading$.value.filter(
                    (item) => item !== `${editablePos?.id}_description`,
                  ),
                );
                return !!state.orderData.positions?.some((el) => el?.id === editablePos?.id);
              }), // if position is deleted
              map((values) => {
                const state = this.stream$.getValue();
                let updatedPos = state.orderData.positions?.find(
                  (pos) => pos?.id === editablePos?.id,
                )!;
                if (values === 'serverError' && editablePos?.id) {
                  updatedPos.price = 0;
                  this.calcPriceError$.next([...this.calcPriceError$.value, editablePos?.id]);
                } else if (typeof values === 'object' && editablePos?.id) {
                  updatedPos = { ...updatedPos, ...values } as IPosition;
                  this.calcPriceError$.next(
                    this.calcPriceError$.value.filter((item) => item !== editablePos?.id),
                  );
                }
                updatedPos.total = this.calculateTotalDependsOnSaleUnit(updatedPos);
                state.orderData.positions = state.orderData.positions?.map((el) =>
                  el?.id === updatedPos?.id ? updatedPos : el,
                );
                this.editedCells.add(`${editablePos?.id}_${action}`);
                this.editedCells.delete(`${editablePos?.id}_priceCell`);
                this.editedCells.delete(`${editablePos?.id}_discountCell`);
                this.editedCells.delete(`${editablePos?.id}_vatCell`);
                return { ...state };
              }),
            );
          }),
          switchMap((state) => {
            if (state.selectedPos?.quantity) {
              this.stream$.next({ ...state, action: 'updateState' });
              return this.getDeliveryCost({ state }); // Recalculate delivery data
            } else return of(state);
          }),
        );
      }),
    );
  }

  /* ORDER CELL METHODS end */

  /* UTILITY start */
  private defaultRound(v: number): number {
    const precision = companyConfigService.getCachedConfigs()?.decimalPlacesCount || 2;
    const multiplier = Math.pow(10, precision);
    return Math.round(v * multiplier) / multiplier;
  }

  private prepareArgsToSave({
    state,
    orderId,
    modificationOptions,
  }: IPrepareArgsToSave): SaveOrderMutationVariables {
    const {
      orderData: {
        customerId,
        orderDate,
        orderType,
        specialAddress,
        referenceOrderNo,
        deliveryTime,
        tourplanId,
        linkText,
        note,
        note2,
        notePlainText,
        positions = [],
      },
      customerInfo: { deliveryAddress },
    } = state || {};

    const formattedPositions = positions.map((position) => ({
      positionId: position?.positionId,
      virtualPositionId: position?.virtualPositionId,
      articleId: position?.articleId,
      quantity: Number(position?.quantity) || 0,
      correctionValue: position?.correctionValue,
      price: position?.price,
      total: position?.total,
      discount: position?.discount,
      vatCode: position?.vatCode,
      indTextDeliveryNote: position?.indTextDeliveryNote,
      indTextProduction: position?.indTextProduction,
      splittingPartsPreset: position?.splittingPartsPreset,
    })) as Wa_OrderPositionInputs[];

    const orderProps = {
      id: String(orderId),
      customerId,
      date: format(localeFormatterHelper.localizedDate(orderDate), 'yyyy-MM-dd'),
      orderType,
      specialAddress: deliveryAddress !== specialAddress ? specialAddress : null,
      referenceOrderNo,
      deliveryTime,
      tourplanId,
      linkText,
      note,
      note2,
      notePlainText,
      positions: formattedPositions,
      modificationOptions: {
        ignoreMinMaxProblems: false,
        ignoreQuantityPerLotProblems: false,
        ignoreSplittingProblems: false,
        ignoreDaysToProduce: false,
        ...modificationOptions,
      },
    } as SaveOrderMutationVariables['orderProps'];

    return {
      orderProps,
    };
  }
  private calculateTotalDependsOnSaleUnit({
    price,
    quantity,
    saleUnit,
  }: {
    price?: number | null;
    quantity?: number | null;
    saleUnit?: C_Sale_Unit | null;
  }): number {
    price = price ? price : 0;
    quantity = quantity ? quantity : 0;
    const kiloAmountRepresentationId =
      companyConfigService.getCachedConfigs()?.kiloAmountRepresentationId;
    if (saleUnit === C_Sale_Unit.SU2_100G) {
      return this.defaultRound((quantity / 100) * price);
    }
    if (
      saleUnit === C_Sale_Unit.SU3_1000G &&
      kiloAmountRepresentationId === C_Kg_Representation.KGR2_1000_IS_1
    ) {
      return this.defaultRound((quantity / 1000) * price);
    }
    return this.defaultRound(quantity * price);
  }
  private getDeliveryCost(args: {
    state: IOrderDetailsState;
    force?: true;
  }): Observable<IOrderDetailsState> {
    const updatedState = structuredClone(args.state);
    const {
      customerInfo: { transportSector },
    } = updatedState;
    const isDeliveryPosition =
      args.force || !!updatedState.orderData.positions?.some((val) => val?.virtualPositionId);
    if (isDeliveryPosition) {
      const deliveryPosition = {
        virtualPositionId: C_Virtual_Position.VP2_DELIVERY_COST,
        quantity: 1,
        vatCode: C_Vat_Code.VT5_ZERO,
        price: 0,
        total: 0,
        weight: 0,
        id: Date.now(),
      } as IPosition;

      if (transportSector?.isPriceRateFixed) {
        const fixedPriceDeliveryPosition = {
          ...deliveryPosition,
          price: transportSector.priceRateFixed,
          total: transportSector.priceRateFixed,
        };
        const fixedPriceDeliveryPositionInd = updatedState.orderData.positions?.findIndex(
          (el) => el?.virtualPositionId,
        );
        if (
          typeof fixedPriceDeliveryPositionInd === 'number' &&
          fixedPriceDeliveryPositionInd !== -1
        ) {
          updatedState.orderData.positions?.splice(
            fixedPriceDeliveryPositionInd,
            1,
            fixedPriceDeliveryPosition,
          );
        } else updatedState.orderData.positions?.push(fixedPriceDeliveryPosition);
        return of(updatedState);
      }
      const totalWeight: number = getTotalWeight(updatedState.orderData.positions);
      if (!totalWeight || !updatedState.orderData.positions?.some((val) => val?.articleId)) {
        const deliveryPositionInd = updatedState.orderData.positions?.findIndex(
          (el) => el?.virtualPositionId,
        );
        if (typeof deliveryPositionInd === 'number' && deliveryPositionInd !== -1) {
          updatedState.orderData.positions?.splice(deliveryPositionInd, 1, deliveryPosition);
        } else updatedState.orderData.positions?.push(deliveryPosition);
        this.deliveryCostLoading$.next(false);
        return of(updatedState);
      }
      return orderListState.sub.sharedValues().pipe(
        tap(({ order }) => {
          this.deliveryCostLoading$.next(true);
          orderService.pub.orderDeliveryCost({
            customerId: order!.customerId,
            totalWeight,
          });
        }),
        mergeMap(() => {
          return orderService.sub.orderDeliveryCost().pipe(
            responseHandler<OrderDeliveryCostRes | 'serverError'>({
              errorReturnType: 'serverError',
            }),
            tap((value) => {
              if (value === 'serverError') this.deliveryCostLoading$.next(false);
            }),
            filter((value) => value !== 'serverError'),
            map((value) => {
              const cost = value as OrderDeliveryCostRes;
              const updatedDeliveryPosition = {
                ...deliveryPosition,
                price: cost,
                total: cost,
                weight: totalWeight,
              };
              const deliveryPositionInd = updatedState.orderData.positions?.findIndex(
                (el) => el?.virtualPositionId,
              );
              if (typeof deliveryPositionInd === 'number' && deliveryPositionInd !== -1) {
                updatedState.orderData.positions?.splice(
                  deliveryPositionInd,
                  1,
                  updatedDeliveryPosition,
                );
              } else updatedState.orderData.positions?.push(updatedDeliveryPosition);
              this.deliveryCostLoading$.next(false);
              return updatedState;
            }),
          );
        }),
      );
    }
    return of(updatedState);
  }
  /**
   * The position as it is *right now*, not as it was when the request went out.
   *
   * A cell's price/discount round-trip takes as long as the backend takes, and
   * during fast order entry the user has moved on inside that window: picked
   * the article for the line, typed a price, changed the VAT code. Merging the
   * response into the snapshot taken BEFORE the request - which is what this
   * replaces - reverted every one of those edits, and that is what "order lines
   * require correction" was (WA-3803). `preUpdateCell` has already written the
   * edited field into state synchronously, so the live row is the correct base
   * and the response only has to contribute what it actually owns.
   *
   * Undefined when the position is gone - deleted while the request was in
   * flight - which the `filter` downstream drops.
   */
  private livePosition(position?: IOrderDetailsState['selectedPos']) {
    if (!position) return undefined;
    return this.stream$.getValue().orderData.positions?.find((pos) => pos?.id === position.id);
  }
  private preUpdateCell(position: IOrderDetailsState['selectedPos']) {
    const state = this.stream$.getValue();
    if (position && state.orderData.positions) {
      state.orderData.positions = state.orderData.positions?.map((el) =>
        el?.id === position.id ? position : el,
      );
      this.stream$.next({ ...state, action: 'updateState' });
    }
  }
  /* UTILITY end */
}
